import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import camelCase from 'lodash/camelCase';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody } from 'reactstrap';

import { postEmail, EMAIL_SUBSCRIBE, EMAIL_ERROR } from 'Redux/ducks/email-subscribe';
import { hasUserEnteredCompetition } from 'Redux/ducks/competition-entry';
import { toggleFeedbackPopup } from 'Redux/ducks/feedback-popups';
import store from 'Redux/store';
import useViewport from 'Hooks/useViewport';
import Countdown from 'Components/EmailCompetition/Countdown';
import FluidImg from 'Components/Utility/FluidImg';

import 'Components/Banner/EmailSignupPlacement/CompetitionBanner/CompetitionBanner.scss';

const messages = defineMessages({
  successTitle: {
    id: 'competition-banner.pop.success.title',
    defaultMessage: 'Success !',
  },
  successMessage: {
    id: 'competition-banner.pop.success.message',
    defaultMessage: 'You are now subscribed to the Fanatical newsletter.',
  },
  errorTitle: {
    id: 'competition-banner.pop.error.title',
    defaultMessage: 'Something went wrong',
  },
  errorMessage: {
    id: 'competition-banner.pop.error.message',
    defaultMessage: 'Whoops something went wrong please try again.',
  },
  enterEmailAddress: {
    id: 'competition-banner.email.placeholder',
    defaultMessage: 'Enter your email',
  },
  competitionTitle: {
    id: 'competition-banner.competitionTitle',
    defaultMessage: 'Win Marvel\'s Spider-Man 2 on Steam!',
  },
  competitionDescription: {
    id: 'competition-banner.competitionDescription',
    defaultMessage: 'Sign up to the Fanatical newsletter for a chance to win 1 of 3 Steam keys for Marvel\'s Spider-Man 2',
  },
});

export default function CompetitionBanner({ appearsOn, bannerType, competitionOverride }) {
  const [newEmail, setNewEmail] = useState('');
  const [modal, setModal] = useState(false);

  const { formatMessage } = useIntl();

  const { isAboveMd, isBelowLg } = useViewport();

  const dispatch = useDispatch();

  const competitions = useSelector(state => state?.competitions);

  const emailSubscribe = useSelector(state => state?.emailSubscribe);
  const authenticated = useSelector(state => state?.auth?.authenticated);
  const authenticatedEmail = useSelector(state => state?.auth?.email);
  const competitionEntry = useSelector(state => state?.competitionEntry);

  const toggle = () => { setModal(!modal); };

  const competition = competitionOverride || competitions[0] || {};
  const competitionId = get(competition, '_id', '');
  const competitionName = get(competition, 'name', '');
  const competitionEndDate = get(competition, 'endDate', '');
  const competitionTitle = get(competition, 'title', '');
  const competitionSubtitle = get(competition, 'subtitle', '');
  const competitionTAndCs = get(competition, 'competitionTAndCs', '');
  const competitionDesktopImage = get(competition, 'imgDesktop', '');
  const competitionMobileImage = get(competition, 'imgMobile', '');
  const competitionPopupImage = get(competition, 'imgPopup', '');
  const competitionTextColour = get(competition, 'textColour', '#FFFFFF');
  const includePopup = get(competition, 'includePopup', false);
  const popupText = get(competition, 'popupText', '');

  const context = `competition_${camelCase(competitionName)}_${camelCase(appearsOn)}`;

  const competitionLS = localStorage.getItem(`competitionSubscribedDate_${competitionId}`);

  // Check if the singed in user has already entered the competition
  useEffect(() => {
    if (competitionId && authenticatedEmail && !competitionLS) {
      dispatch(hasUserEnteredCompetition(competitionId, authenticatedEmail));
    }
  }, [competitionId, authenticatedEmail, dispatch, competitionLS]);

  // Used to trigger the feedback popups
  useEffect(() => {
    if (emailSubscribe.currentState === EMAIL_SUBSCRIBE && emailSubscribe.context === context) {
      dispatch(toggleFeedbackPopup({ type: 'success', title: formatMessage(messages.successTitle), message: formatMessage(messages.successMessage) }));
      localStorage.setItem(`competitionSubscribedDate_${competitionId}`, Date.now());
    }

    if (emailSubscribe.currentState === EMAIL_ERROR && emailSubscribe.context === context) {
      dispatch(toggleFeedbackPopup({ type: 'error', title: formatMessage(messages.errorTitle), message: formatMessage(messages.errorMessage) }));
    }
  }, [
    dispatch,
    emailSubscribe.currentState,
    emailSubscribe.context,
    context,
    competitionId,
    formatMessage,
  ]);

  const optinEmail = (event, emailPassed, optin) => {
    event.preventDefault();
    store.dispatch(postEmail(emailPassed, context, optin, true, null, competition._id));
  };

  const handleChange = (event) => {
    event.preventDefault();
    setNewEmail(event.target.value, false);
  };

  const handleSubmit = (event) => {
    if (authenticated) {
      return optinEmail(event, authenticatedEmail, true);
    }
    return optinEmail(event, newEmail, true);
  };

  const isAlreadyEntered = !!localStorage.getItem(`competitionSubscribedDate_${competitionId}`) || competitionEntry.includes(competitionId);

  return (
    <>
      <article className={`CompetitionBanner CompetitionBanner--${bannerType}`}>
        <div className="CompetitionBanner__content">
          <div className="CompetitionBanner__content__copy">
            <div className="CompetitionBanner__content__copy__title" style={{ color: competitionTextColour }}>
              {competitionTitle || formatMessage(messages.competitionTitle)}
            </div>
            <div className="CompetitionBanner__content__copy__description" style={{ color: competitionTextColour }}>
              {competitionSubtitle || formatMessage(messages.competitionDescription)}
              {includePopup &&
                <button className="CompetitionBanner__content__copy__description__button" style={{ color: competitionTextColour }} onClick={() => toggle()}>
                  <FormattedMessage id="competition-banner.button.find-out-more" defaultMessage="Find out more." />
                </button>
              }
            </div>
            {bannerType === 'offer' &&
              <Countdown targetDate={competitionEndDate} textColor={competitionTextColour} />
            }
          </div>
          <div className="CompetitionBanner__content__actions">
            {bannerType !== 'offer' &&
              <Countdown targetDate={competitionEndDate} textColor={competitionTextColour} />
            }
            {isAlreadyEntered &&
              <div className="CompetitionBanner__content__actions__entered">
                <FontAwesomeIcon icon={faCircleCheck} />
                <FormattedMessage id="competition-banner.entered" defaultMessage="Competition Entered" />
              </div>
            }
            {!isAlreadyEntered &&
              <form className="CompetitionBanner__content__actions__form" onSubmit={handleSubmit}>
                <div className="input-group">
                  <input
                    required
                    placeholder={formatMessage(messages.enterEmailAddress)}
                    className="form-control"
                    type="email"
                    name="email"
                    title="Enter your email address"
                    enterKeyHint="send"
                    aria-label="email"
                    value={authenticatedEmail}
                    hidden={!!authenticatedEmail}
                    onChange={handleChange}
                  />
                  <span className={classNames(authenticatedEmail ? 'input-group-append-hidden' : 'input-group-append')}>
                    <button className="btn btn-primary" type="submit">
                      <FormattedMessage
                        id="competition-banner.modal.button"
                        defaultMessage="Subscribe & enter"
                      />
                    </button>
                  </span>
                </div>
              </form>
            }
            <a
              className="CompetitionBanner__content__actions__terms"
              href={competitionTAndCs || 'https://support.fanatical.com/hc/en-us/articles/360018002197-Promotions-Summary-Terms-and-Conditions'}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: competitionTextColour }}
            >
              <FormattedMessage id="competition-banner.terms" defaultMessage="Terms & conditions apply." />
            </a>
          </div>
        </div>

        <div className="CompetitionBanner__background">
          {isAboveMd && competitionDesktopImage && <FluidImg src={competitionDesktopImage} alt="Background" sizesOverride="100vw" />}
          {isBelowLg && competitionMobileImage && <FluidImg src={competitionMobileImage} alt="Background" sizesOverride="100vw" />}
        </div>
      </article>

      <Modal isOpen={modal} toggle={toggle} className="SiteModal">
        <ModalBody className="SiteModal__main">
          {popupText && (
            <div className="SiteModal__main__title">
              {popupText}
            </div>
          )}
          {competitionPopupImage && (
            <FluidImg
              sizes="100vw"
              src={`${competitionPopupImage}`}
              className="SiteModal__main__mainImg"
              alt="Winter Sleigh-ale Giveaway | Win big in Winter Sale! Enter for a chance to in one of five prizes | Contest ends 6 January 2025 4pm BST. You can unsubscribe via the newsletter at any time. By subscribing to our newsletter you agree to our Privacy Policy. T&Cs apply."
            />
          )}
          <div className="SiteModal__main__smallprint">
            <a
              className="CompetitionBanner__content__actions__terms"
              href={competition.competitionTAndCs || 'https://support.fanatical.com/hc/en-us/articles/360018002197-Promotions-Summary-Terms-and-Conditions'}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FormattedMessage id="competition-banner.modal.terms" defaultMessage="Terms & conditions apply." />
            </a>
          </div>
        </ModalBody>
        <div className="SiteModal__closeButton">
          <button aria-label="close" onClick={() => toggle()}>
            <FontAwesomeIcon icon={faXmark} title="Close modal" />
          </button>
        </div>
      </Modal>
    </>
  );
}

CompetitionBanner.propTypes = {
  appearsOn: PropTypes.oneOf([
    'home',
    'receipt',
    'product',
    'blog_main',
    'blog_article',
    'community',
    'gift_guide',
    'fantasyverse',
    'on_sale',
    'product_offers',
    'the_game_awards',
  ]).isRequired,
  bannerType: PropTypes.oneOf([
    'portrait',
    'landscape',
    'offer',
  ]).isRequired,
};
